import useCallPoliticalMentionsByTopic from 'components/ContentRelated/PoliticalMentionsWidget/useCallPoliticalMentionsByTopic';
import { createAnalyticsState } from 'components/Dashboard/Analytics/createAnalyticsState';
import { compileData } from 'components/Dashboard/Analytics/hooks/useCompileMentionsDataFunctions';
import MentionsGraphHoverLink from 'components/Dashboard/components/MentionsGraphHoverLink';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import React, { useRef, useEffect, useCallback, useState } from 'react';

const BlockchainKeywordContainer = ({ keyword, token, enableAnalytics, setShowAnalyticsPopup, isAuthenticated }) => {
  const containerRef = useRef(null);
  const dotsContainerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const dotsRef = useRef([]);
  const lineChunksRef = useRef([]);
  const [interleavedElements, setInterleavedElements] = useState([]);
  const [isOpenMentionsGraph, setIsOpenMentionsGraph] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dataRef = useRef({});
  const [coordinates, setCoordinates] = useState({});
  const [showDots, setShowDots] = useState(true);
  const pendingDotsToAnimateRef = useRef(null);
  const isHoverAboveRef = useRef(false);
  const isHoveringHoverBoxRef = useRef(false);
  const closeDelayTimeoutRef = useRef(null);
  const oneShotHoverWaveRef = useRef(false);
  const clickAnimStateRef = useRef({
    running: false,
    proxyNodes: [],
    stepTimeoutIds: [],
    finishTimeoutId: null,
    hideDotsTimeoutId: null,
  });
  const TOP_ADJUSTMENT = 9;

  const isMobile = useIsMobile();

  const callResults = useRef(null);
  const { callPoliticalMentionsByTopic } = useCallPoliticalMentionsByTopic();
  callResults.current = async () => {
    const source = new AbortController();
    try {
      const analyticsState = createAnalyticsState();
      setIsLoading(true);
      const results = await callPoliticalMentionsByTopic({
        freeSearch: ChangeSearchQuery(keyword),
        token,
        source,
      });
      if (results) {
        let value = compileData(results.data, analyticsState).value;
        let resData = { ...results.data, value };
        dataRef.current = resData;
        setIsLoading(false);
      }
    } catch (error) {}
  };

  const hoverMentionsGraphBox = useRef(null);

  // Note: no ancestor-scale helpers needed with proxy-dot approach

  // Relative offsets (in px) from MentionsGraphHoverLink top-left corner
  const DOT_TARGET_OFFSETS = [
    { x: 28, y: 47 }, // Dot 0
    { x: 69, y: 47 }, // Dot 1
    { x: 110, y: 47 }, // Dot 2
    { x: 150, y: 47 }, // Dot 3
    { x: 191, y: 47 }, // Dot 4
    { x: 232, y: 47 }, // Dot 5
  ];

  // Function to animate dots to specific positions relative to hover box
  const animateDotsToPositions = useCallback((dotsToAnimate, stateRef) => {
    // Capture original viewport positions before animation
    const originalPositions = dotsToAnimate.map((dot) => {
      const rect = dot.getBoundingClientRect();
      return { x: rect.left, y: rect.top };
    });

    // Set line chunks margin to 0 and change color immediately during animation
    lineChunksRef.current.forEach((lineChunk) => {
      lineChunk.style.backgroundColor = '#0094cc';
      lineChunk.style.transition = `margin ${dotsToAnimate.length * 50}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
    });

    // Build portal-safe proxies above the tooltip
    const proxies = [];
    if (stateRef) {
      stateRef.current.proxyNodes = [];
      stateRef.current.stepTimeoutIds = [];
    }
    dotsToAnimate.forEach((dot, index, array) => {
      const hasOffset = !!DOT_TARGET_OFFSETS[index];
      if (!hasOffset) return;

      // Hide original during animation
      dot.style.opacity = '0';

      // Create proxy element
      const proxy = document.createElement('span');
      proxy.className = 'blockchain-dot';
      proxy.style.position = 'fixed';
      proxy.style.left = `${originalPositions[index].x}px`;
      proxy.style.top = `${originalPositions[index].y + 2}px`;
      // Ensure visibility without relying on nested stylesheet context
      proxy.style.width = '6px';
      proxy.style.height = '6px';
      proxy.style.borderRadius = '50%';
      proxy.style.display = 'block';
      proxy.style.transform = 'translate(0, 0)';
      proxy.style.transition = `transform ${
        array.length * 50
      }ms cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color ${
        array.length * 50
      }ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
      proxy.style.backgroundColor = `#0094cc`;
      // Ensure above any portal content
      proxy.style.zIndex = '2147483647';
      proxy.style.pointerEvents = 'none';
      document.body.appendChild(proxy);
      proxies.push({ proxy, index });
      if (stateRef) stateRef.current.proxyNodes.push(proxy);

      // Compute viewport delta to target
      let dx = 0;
      let dy = 0;
      if (hoverMentionsGraphBox.current) {
        const hoverRect = hoverMentionsGraphBox.current.getBoundingClientRect();
        const tx = hoverRect.left + DOT_TARGET_OFFSETS[index].x;
        const aboveOffsetFix = isHoverAboveRef.current ? TOP_ADJUSTMENT : 0;
        const ty = hoverRect.top + DOT_TARGET_OFFSETS[index].y - aboveOffsetFix;
        dx = tx - originalPositions[index].x;
        dy = ty - originalPositions[index].y;
      }

      const run = () => {
        proxy.style.transform = `translate(${dx}px, ${dy}px) scale(1.25)`;
        proxy.style.backgroundColor = '#00122b';
      };
      if (index === 0) run();
      else {
        const id = setTimeout(run, index * 50);
        if (stateRef) stateRef.current.stepTimeoutIds.push(id);
      }
    });

    const totalAnimationDuration = dotsToAnimate.length * 50 + dotsToAnimate.length * 50;
    const finishId = setTimeout(() => {
      // Restore originals
      dotsToAnimate.forEach((dot) => {
        dot.style.opacity = '';
        dot.style.transition = 'none';
        dot.style.transform = 'none';
      });
      // Remove proxies
      proxies.forEach(({ proxy }) => {
        if (proxy && proxy.parentNode) proxy.parentNode.removeChild(proxy);
      });
      // Reset line chunks
      lineChunksRef.current.forEach((lineChunk) => {
        lineChunk.style.marginLeft = '';
        lineChunk.style.marginRight = '';
        lineChunk.style.backgroundColor = '';
        lineChunk.style.transition = '';
      });
      if (stateRef) stateRef.current.finishTimeoutId = null;
      if (stateRef) stateRef.current.running = false;
    }, totalAnimationDuration);
    if (stateRef) stateRef.current.finishTimeoutId = finishId;
  }, []);

  const cleanupClickAnimation = useCallback(() => {
    const s = clickAnimStateRef.current;
    // Cancel scheduled animation frames
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Cancel stagger timeouts
    if (s.stepTimeoutIds && s.stepTimeoutIds.length) {
      s.stepTimeoutIds.forEach((id) => clearTimeout(id));
      s.stepTimeoutIds = [];
    }
    // Cancel final cleanup timeout
    if (s.finishTimeoutId) {
      clearTimeout(s.finishTimeoutId);
      s.finishTimeoutId = null;
    }
    // Cancel hide-dots timeout
    if (s.hideDotsTimeoutId) {
      clearTimeout(s.hideDotsTimeoutId);
      s.hideDotsTimeoutId = null;
    }
    // Remove proxies
    if (s.proxyNodes && s.proxyNodes.length) {
      s.proxyNodes.forEach((proxy) => {
        if (proxy && proxy.parentNode) proxy.parentNode.removeChild(proxy);
      });
      s.proxyNodes = [];
    }

    // Restore dots immediately
    setShowDots(true);
    dotsRef.current.forEach((dot) => {
      dot.style.opacity = '1';
      dot.style.visibility = 'visible';
      dot.style.transition = 'none';
      dot.style.transform = 'none';
      dot.style.backgroundColor = '#667180';
      setTimeout(() => {
        dot.style.transition = 'transform 100ms ease, background-color 100ms ease';
      }, 0);
    });
    // Reset line chunks
    lineChunksRef.current.forEach((lineChunk) => {
      lineChunk.style.transition = 'none';
      lineChunk.style.backgroundColor = '#667180';
      lineChunk.style.marginLeft = '';
      lineChunk.style.marginRight = '';
      setTimeout(() => {
        lineChunk.style.transition = 'background-color 100ms ease';
      }, 0);
    });

    // Reset state
    s.running = false;
    pendingDotsToAnimateRef.current = null;
  }, []);

  // Handle click event
  const handleClick = useCallback(
    (e) => {
      if (!enableAnalytics && isAuthenticated) {
        setShowAnalyticsPopup(true);
        return;
      }
      if (isMobile) {
        // On mobile: trigger the hover wave animation on click instead of hover handlers
        // and do not open the mentions graph popover
        // Reuse hover animation logic by invoking mouse enter handler body
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        // Run a one-shot wave regardless of :hover state
        oneShotHoverWaveRef.current = true;

        // Begin hover animation logic (extracted inline from handleMouseEnter)
        const isOpen =
          document.querySelector('.hover-mentions-graph-message-container')?.style?.visibility === 'visible';
        if (isOpen) {
          return;
        }
        const notEnabledAnalitycsCondition = !enableAnalytics && isAuthenticated;
        if (Object.keys(dataRef.current).length === 0 && !notEnabledAnalitycsCondition) {
          callResults.current();
        }

        dotsRef.current.forEach((dot) => {
          dot.style.transition = 'none';
          dot.dataset.completed = 'false';
        });
        lineChunksRef.current.forEach((lineChunk) => {
          lineChunk.style.transition = 'none';
          lineChunk.dataset.completed = 'false';
        });

        let startTime = Date.now();
        const duration = 200;

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const totalDuration = duration + (dotsRef.current.length - 1) * 100;

          if (elapsed >= totalDuration) {
            dotsRef.current.forEach((dot) => {
              dot.style.transform = 'translateY(0) translateX(0)';
              dot.style.backgroundColor = '#0094cc';
              dot.style.transition = 'transform 0.125s ease, background-color 0.125s ease';
            });
            lineChunksRef.current.forEach((lineChunk) => {
              lineChunk.style.backgroundColor = '#0094cc';
              lineChunk.style.transition = 'background-color 0.125s ease';
            });
            oneShotHoverWaveRef.current = false;
            return;
          }

          dotsRef.current.forEach((dot, index) => {
            if (dot.dataset.completed === 'true') {
              return;
            }
            const delay = index * 100;
            const adjustedProgress = Math.max(0, Math.min(1, (elapsed - delay) / duration));
            if (adjustedProgress > 0 && adjustedProgress < 1) {
              const waveOffset = Math.sin(adjustedProgress * Math.PI * 2) * 4 * -1;
              dot.style.transform = `translateY(${waveOffset < 0 ? waveOffset : 0}px)`;
              dot.style.backgroundColor = '#0094cc';
            } else if (adjustedProgress >= 1) {
              dot.style.transform = 'translateY(0) translateX(0)';
              dot.style.backgroundColor = '#0094cc';
              dot.style.transition = 'none';
              dot.dataset.completed = 'true';
            } else {
              dot.style.transform = 'translateY(0) translateX(0)';
              dot.style.backgroundColor = '#667180';
            }
          });

          lineChunksRef.current.forEach((lineChunk, index) => {
            if (lineChunk.dataset.completed === 'true') {
              return;
            }
            const delay = index * 100;
            const adjustedProgress = Math.max(0, Math.min(1, (elapsed - delay) / duration));
            if (adjustedProgress === 0) {
              lineChunk.style.backgroundColor = '#667180';
            } else {
              lineChunk.style.backgroundColor = '#0094cc';
              lineChunk.dataset.completed = 'true';
            }
          });

          const shouldContinue = oneShotHoverWaveRef.current ? elapsed < totalDuration : false;
          if (shouldContinue) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            dotsRef.current.forEach((dot) => {
              dot.style.transition = 'none';
              dot.style.transform = 'translateY(0) translateX(0)';
              dot.style.backgroundColor = '#667180';
              setTimeout(() => {
                dot.style.transition = 'transform 100ms ease, background-color 100ms ease';
              }, 0);
            });
            lineChunksRef.current.forEach((lineChunk) => {
              lineChunk.style.transition = 'none';
              lineChunk.style.backgroundColor = '#667180';
              setTimeout(() => {
                lineChunk.style.transition = 'background-color 100ms ease';
              }, 0);
            });
          }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        // After the one-shot wave, continue with click animation
        const waveDuration = 200;
        const totalWaveDuration = waveDuration + (dotsRef.current.length - 1) * 100;
        const afterWaveId = setTimeout(() => {
          // If gated, show analytics popup and stop
          if (!enableAnalytics && isAuthenticated) {
            setShowAnalyticsPopup(true);
            return;
          }
          setIsOpenMentionsGraph(true);

          const maxCount = Math.min(6, DOT_TARGET_OFFSETS.length, dotsRef.current.length);
          const dotsToAnimate = dotsRef.current.slice(0, maxCount);
          const shouldSkipDotAnimation = Array.isArray(dataRef.current?.value) && dataRef.current.value.length === 0;

          const scheduleHideDots = (count) => {
            const totalAnimationDuration = count * 50 + count * 50;
            const id = setTimeout(() => {
              clickAnimStateRef.current.hideDotsTimeoutId = null;
              setShowDots(false);
            }, totalAnimationDuration);
            clickAnimStateRef.current.hideDotsTimeoutId = id;
          };

          const runAnimationNow = () => {
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current);
              animationFrameRef.current = null;
            }
            animationFrameRef.current = requestAnimationFrame(() => {
              calculcatePosition();
              animationFrameRef.current = requestAnimationFrame(() => {
                clickAnimStateRef.current.running = true;
                animateDotsToPositions(dotsToAnimate, clickAnimStateRef);
                scheduleHideDots(dotsToAnimate.length);
              });
            });
          };

          if (isLoading) {
            pendingDotsToAnimateRef.current = dotsToAnimate;
          } else if (!shouldSkipDotAnimation) {
            runAnimationNow();
          }
        }, totalWaveDuration);
        // Track timeout for cleanup
        clickAnimStateRef.current.stepTimeoutIds.push(afterWaveId);
        return;
      }
      setIsOpenMentionsGraph(true);
      const maxCount = Math.min(6, DOT_TARGET_OFFSETS.length, dotsRef.current.length);
      const dotsToAnimate = dotsRef.current.slice(0, maxCount);
      const shouldSkipDotAnimation = Array.isArray(dataRef.current?.value) && dataRef.current.value.length === 0;
      const scheduleHideDots = (count) => {
        const totalAnimationDuration = count * 50 + count * 50;
        const id = setTimeout(() => {
          clickAnimStateRef.current.hideDotsTimeoutId = null;
          setShowDots(false);
        }, totalAnimationDuration);
        clickAnimStateRef.current.hideDotsTimeoutId = id;
      };
      // Cancel any ongoing hover animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Reset any hover animation state
      dotsRef.current.forEach((dot) => {
        dot.style.transition = 'none';
        dot.style.transform = 'translateY(0) translateX(0)';
        dot.style.backgroundColor = '#667180';
        dot.dataset.completed = 'false';
      });
      lineChunksRef.current.forEach((lineChunk) => {
        lineChunk.style.transition = 'none';
        lineChunk.style.backgroundColor = '#667180';
        lineChunk.dataset.completed = 'false';
      });

      const runAnimationNow = () => {
        // Animate dots to positions relative to hover box
        // Ensure the hover box is mounted and fully laid out with final clamped position
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        animationFrameRef.current = requestAnimationFrame(() => {
          calculcatePosition();
          animationFrameRef.current = requestAnimationFrame(() => {
            clickAnimStateRef.current.running = true;
            animateDotsToPositions(dotsToAnimate, clickAnimStateRef);
            scheduleHideDots(dotsToAnimate.length);
          });
        });
      };

      // If the hover box won't be visible yet (loading), defer until it's open
      if (isLoading) {
        pendingDotsToAnimateRef.current = dotsToAnimate;
      } else if (!shouldSkipDotAnimation) {
        runAnimationNow();
      } else {
        // Skip animation for empty data sets; keep dots visible
        return;
      }
    },
    [animateDotsToPositions, isAuthenticated, enableAnalytics, isLoading, isMobile]
  );

  // If a click happened while loading, run the animation after the hover box opens
  useEffect(() => {
    const isHoverVisible = isOpenMentionsGraph && !isLoading;
    if (!isHoverVisible) return;
    if (!pendingDotsToAnimateRef.current || pendingDotsToAnimateRef.current.length === 0) return;

    const dotsToAnimate = pendingDotsToAnimateRef.current;
    pendingDotsToAnimateRef.current = null;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // If data is empty, skip any deferred click animation
    const isEmpty = Array.isArray(dataRef.current?.value) && dataRef.current.value.length === 0;
    if (isEmpty) {
      pendingDotsToAnimateRef.current = null;
      return;
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      calculcatePosition();
      animationFrameRef.current = requestAnimationFrame(() => {
        clickAnimStateRef.current.running = true;
        animateDotsToPositions(dotsToAnimate, clickAnimStateRef);
        const totalAnimationDuration = dotsToAnimate.length * 50 + dotsToAnimate.length * 50;
        const id = setTimeout(() => {
          clickAnimStateRef.current.hideDotsTimeoutId = null;
          setShowDots(false);
        }, totalAnimationDuration);
        clickAnimStateRef.current.hideDotsTimeoutId = id;
      });
    });
  }, [isOpenMentionsGraph, isLoading, animateDotsToPositions]);

  // Animation logic for hover
  const handleMouseEnter = useCallback(
    (e) => {
      calculcatePosition();

      const isOpen = document.querySelector('.hover-mentions-graph-message-container')?.style?.visibility === 'visible';
      if (isOpen) {
        return;
      }
      const notEnabledAnalitycsCondition = !enableAnalytics && isAuthenticated;
      if (Object.keys(dataRef.current).length === 0 && !notEnabledAnalitycsCondition) {
        callResults.current();
      }

      // Disable transitions immediately to avoid interference on first hover
      dotsRef.current.forEach((dot) => {
        dot.style.transition = 'none';
        dot.dataset.completed = 'false';
      });
      lineChunksRef.current.forEach((lineChunk) => {
        lineChunk.style.transition = 'none';
        lineChunk.dataset.completed = 'false';
      });

      let startTime = Date.now();
      const duration = 200;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const totalDuration = duration + (dotsRef.current.length - 1) * 100;

        if (elapsed >= totalDuration) {
          dotsRef.current.forEach((dot) => {
            dot.style.transform = 'translateY(0) translateX(0)';
            dot.style.backgroundColor = '#0094cc';
            dot.style.transition = 'transform 0.125s ease, background-color 0.125s ease';
          });
          lineChunksRef.current.forEach((lineChunk) => {
            lineChunk.style.backgroundColor = '#0094cc';
            lineChunk.style.transition = 'background-color 0.125s ease';
          });
          return;
        }

        // Apply single-cycle wave motion to dots
        dotsRef.current.forEach((dot, index) => {
          if (dot.dataset.completed === 'true') {
            return;
          }

          const delay = index * 100;
          const adjustedProgress = Math.max(0, Math.min(1, (elapsed - delay) / duration));

          if (adjustedProgress > 0 && adjustedProgress < 1) {
            const waveOffset = Math.sin(adjustedProgress * Math.PI * 2) * 4 * -1;
            dot.style.transform = `translateY(${waveOffset < 0 ? waveOffset : 0}px)`;
            dot.style.backgroundColor = '#0094cc';
          } else if (adjustedProgress >= 1) {
            dot.style.transform = 'translateY(0) translateX(0)';
            dot.style.backgroundColor = '#0094cc';
            dot.style.transition = 'none';
            dot.dataset.completed = 'true';
          } else {
            dot.style.transform = 'translateY(0) translateX(0)';
            dot.style.backgroundColor = '#667180';
          }
        });

        // Change line chunk colors progressively
        lineChunksRef.current.forEach((lineChunk, index) => {
          if (lineChunk.dataset.completed === 'true') {
            return;
          }

          const delay = index * 100;
          const adjustedProgress = Math.max(0, Math.min(1, (elapsed - delay) / duration));

          if (adjustedProgress === 0) {
            lineChunk.style.backgroundColor = '#667180';
          } else {
            lineChunk.style.backgroundColor = '#0094cc';
            lineChunk.dataset.completed = 'true';
          }
        });

        if (containerRef.current?.matches(':hover')) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          // Reset positions and colors instantly
          dotsRef.current.forEach((dot) => {
            dot.style.transition = 'none';
            dot.style.transform = 'translateY(0) translateX(0)';
            dot.style.backgroundColor = '#667180';
            setTimeout(() => {
              dot.style.transition = 'transform 100ms ease, background-color 100ms ease';
            }, 0);
          });
          lineChunksRef.current.forEach((lineChunk) => {
            lineChunk.style.transition = 'none';
            lineChunk.style.backgroundColor = '#667180';
            setTimeout(() => {
              lineChunk.style.transition = 'background-color 100ms ease';
            }, 0);
          });
        }
      };

      animate();
    },
    [enableAnalytics, isAuthenticated]
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(
    (e) => {
      // If click animation is running or scheduled, stop and fully cleanup
      if (
        clickAnimStateRef.current.running ||
        clickAnimStateRef.current.finishTimeoutId ||
        clickAnimStateRef.current.hideDotsTimeoutId
      ) {
        cleanupClickAnimation();
        setIsOpenMentionsGraph(false);
        return;
      }

      // If leaving the container and not immediately entering the hover box, delay-close a bit
      if (closeDelayTimeoutRef.current) {
        clearTimeout(closeDelayTimeoutRef.current);
        closeDelayTimeoutRef.current = null;
      }
      const shouldScheduleClose =
        e.relatedTarget &&
        e.relatedTarget instanceof Node &&
        hoverMentionsGraphBox.current &&
        !hoverMentionsGraphBox.current.contains(e.relatedTarget);
      if (shouldScheduleClose) {
        closeDelayTimeoutRef.current = setTimeout(() => {
          closeDelayTimeoutRef.current = null;
          if (!isHoveringHoverBoxRef.current) {
            setIsOpenMentionsGraph(false);
            setShowDots(true);
            dotsRef.current.forEach((dot) => {
              dot.style.opacity = '1';
              dot.style.visibility = 'visible';
            });
          }
        }, 100);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Reset positions and colors instantly
      dotsRef.current.forEach((dot) => {
        dot.style.transition = 'none';
        dot.style.transform = 'translateY(0) translateX(0)';
        dot.style.backgroundColor = '#667180';
        setTimeout(() => {
          dot.style.transition = 'transform 100ms ease, background-color 100ms ease';
        }, 0);
      });
      lineChunksRef.current.forEach((lineChunk) => {
        lineChunk.style.transition = 'none';
        lineChunk.style.backgroundColor = '#667180';
        setTimeout(() => {
          lineChunk.style.transition = 'background-color 100ms ease';
        }, 0);
      });
    },
    [cleanupClickAnimation]
  );

  // Initialize dots and line chunks
  useEffect(() => {
    if (!containerRef.current || !dotsContainerRef.current) return;

    // Get the text element (em.policymogul-keyword-match) to calculate its width
    const textElement = containerRef.current.querySelector('.policymogul-keyword-match');
    const textWidth = textElement
      ? textElement.offsetWidth || textElement.getBoundingClientRect().width
      : containerRef.current.offsetWidth || containerRef.current.getBoundingClientRect().width;
    dotsContainerRef.current.style.width = `${textWidth}px`;

    const dotDiameter = 6;
    const minSpacing = 14;
    const maxSpacing = 16;

    let dotCount = Math.max(2, Math.floor((textWidth + minSpacing) / (dotDiameter + minSpacing)));
    const totalDotWidth = dotCount * dotDiameter;
    const availableSpaceForGaps = textWidth - totalDotWidth;
    const averageSpacing = dotCount > 1 ? availableSpaceForGaps / (dotCount - 1) : 0;

    if (averageSpacing > maxSpacing && dotCount > 2) {
      dotCount = Math.max(2, Math.floor((textWidth + maxSpacing) / (dotDiameter + maxSpacing)));
    }

    // Cap maximum number of dots
    dotCount = Math.min(6, dotCount);

    // Create interleaved elements (dot, line chunk, dot, line chunk, etc.)
    const interleavedElements = [];

    for (let i = 0; i < dotCount; i++) {
      // Add dot
      interleavedElements.push({
        id: `dot-${i}`,
        type: 'dot',
        className: 'blockchain-dot',
        dataIndex: i,
      });

      // Add line chunk after dot (except for the last dot)
      if (i < dotCount - 1) {
        interleavedElements.push({
          id: `line-${i}`,
          type: 'line',
          className: 'blockchain-line-chunk',
          dataIndex: i,
        });
      }
    }

    setInterleavedElements(interleavedElements);
  }, []);

  // Update refs when interleaved elements change
  useEffect(() => {
    if (dotsContainerRef.current) {
      dotsRef.current = Array.from(dotsContainerRef.current.querySelectorAll('.blockchain-dot'));
      lineChunksRef.current = Array.from(dotsContainerRef.current.querySelectorAll('.blockchain-line-chunk'));
    }
  }, [interleavedElements]);

  const calculcatePosition = () => {
    if (!!containerRef && hoverMentionsGraphBox?.current) {
      const topAdjustment = TOP_ADJUSTMENT;
      const edgeMargin = 16; // keep 15–20px gap to viewport edges

      const targetRect = containerRef.current.getBoundingClientRect();
      const hoverRect = hoverMentionsGraphBox.current.getBoundingClientRect();
      const viewWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;

      // Default below target
      let top = targetRect.top + targetRect.height;
      let left = targetRect.left;
      let paddingTop = topAdjustment;
      let paddingBottom = undefined;

      // Vertical: if overflow bottom, place above; otherwise clamp within margins
      let placedAbove = false;
      if (top + hoverRect.height > viewHeight - edgeMargin) {
        top = Math.max(edgeMargin, targetRect.top - hoverRect.height - topAdjustment);
        paddingBottom = topAdjustment;
        paddingTop = undefined;
        placedAbove = true;
      } else if (top < edgeMargin) {
        top = edgeMargin;
      }

      // Horizontal: clamp within margins
      const maxLeft = viewWidth - hoverRect.width - edgeMargin;
      left = Math.min(Math.max(left, edgeMargin), Math.max(edgeMargin, maxLeft));

      isHoverAboveRef.current = placedAbove;
      setCoordinates({ top, left, paddingTop, paddingBottom });
    }
  };

  // Recalculate position when opening, when loading state changes, and on viewport/box size changes
  useEffect(() => {
    if (!(isOpenMentionsGraph && !isLoading)) return;

    const recalc = () => calculcatePosition();

    // Next frame to ensure DOM has applied layout
    const rafId = requestAnimationFrame(recalc);

    // Track hover box size changes
    const box = hoverMentionsGraphBox.current;
    let resizeObserver;
    if (box && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        recalc();
      });
      resizeObserver.observe(box);
    }

    // Update on window resize/scroll
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);

    return () => {
      cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [isOpenMentionsGraph, isLoading]);

  const hideActions = () => {
    if (isMobile) {
      cleanupClickAnimation();
      setIsOpenMentionsGraph(false);
    }
  };

  useOutsideElement(containerRef, hideActions);

  // Ensure we hide/clear hover link and animations on unmount/navigation
  const closeMentionsGraph = useCallback(() => {
    cleanupClickAnimation();
    setIsOpenMentionsGraph(false);
    setShowDots(true);
    if (dotsRef.current && dotsRef.current.length) {
      dotsRef.current.forEach((dot) => {
        dot.style.opacity = '1';
        dot.style.visibility = 'visible';
        dot.style.transition = 'none';
        dot.style.transform = 'none';
        dot.style.backgroundColor = '#667180';
        setTimeout(() => {
          dot.style.transition = 'transform 100ms ease, background-color 100ms ease';
        }, 0);
      });
    }
    if (lineChunksRef.current && lineChunksRef.current.length) {
      lineChunksRef.current.forEach((lineChunk) => {
        lineChunk.style.transition = 'none';
        lineChunk.style.backgroundColor = '#667180';
        lineChunk.style.marginLeft = '';
        lineChunk.style.marginRight = '';
        setTimeout(() => {
          lineChunk.style.transition = 'background-color 100ms ease';
        }, 0);
      });
    }
  }, [cleanupClickAnimation]);

  useEffect(() => {
    return () => {
      if (closeDelayTimeoutRef.current) {
        clearTimeout(closeDelayTimeoutRef.current);
        closeDelayTimeoutRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      pendingDotsToAnimateRef.current = null;
      closeMentionsGraph();
    };
  }, [closeMentionsGraph]);

  return (
    <>
      <span
        ref={containerRef}
        className={`blockchain-keyword-container ${
          isOpenMentionsGraph && !isLoading ? 'blockchain-keyword-container-is-open' : ''
        }`}
        onClick={handleClick}
        onMouseEnter={!isMobile ? handleMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      >
        <em className='policymogul-keyword-match'>{keyword}</em>
        <div ref={dotsContainerRef} className='blockchain-dots-container'>
          {interleavedElements.map((element) => {
            if (element.type === 'dot') {
              return (
                <span
                  key={element.id}
                  className={element.className}
                  data-index={element.dataIndex}
                  style={{ visibility: showDots ? 'visible' : 'hidden' }}
                />
              );
            } else {
              return (
                <div
                  key={element.id}
                  className={element.className}
                  data-index={element.dataIndex}
                  style={{
                    visibility: !isOpenMentionsGraph ? 'visible' : 'hidden',
                  }}
                />
              );
            }
          })}
          <div
            className='blockchain-line-chunk-full-width'
            style={{ visibility: isOpenMentionsGraph ? 'visible' : 'hidden' }}
          />
        </div>
      </span>
      <MentionsGraphHoverLink
        isOpen={isOpenMentionsGraph && !isLoading}
        closeFunction={closeMentionsGraph}
        keyword={keyword}
        hoverMentionsGraphBox={hoverMentionsGraphBox}
        containerRef={containerRef}
        data={dataRef.current}
        coordinates={coordinates}
        onHoverEnter={() => {
          isHoveringHoverBoxRef.current = true;
          if (closeDelayTimeoutRef.current) {
            clearTimeout(closeDelayTimeoutRef.current);
            closeDelayTimeoutRef.current = null;
          }
        }}
        onHoverLeave={() => {
          isHoveringHoverBoxRef.current = false;
        }}
        enableAnalytics={enableAnalytics}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
};

export default BlockchainKeywordContainer;
